import { createContext, useCallback, useContext, useState, useEffect } from "react";
import { getLoggedUser } from "../services/userService";
import { useApiClient } from "../hooks/useApiClient";
import { ILoggedUser } from "../features/user/interfaces/IUserInterface";
import { useIsAuthenticated } from "@azure/msal-react";

interface UserContextType {
  user: ILoggedUser | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ILoggedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { call, ready } = useApiClient();
  const isAuthenticated = useIsAuthenticated();

  const fetchUser = useCallback(async () => {
    if (!ready || !isAuthenticated) return;

    let isRedirectingToError = false;
    setLoading(true);

    try {
      const data = await call(getLoggedUser);
      setUser(data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setUser(null);
      } else {
        isRedirectingToError = true;
        console.error("Error fetching logged user:", error);
      }
    } finally {
      // The API interceptor navigates non-404 failures to /error. Keep the
      // auth guard pending so it cannot briefly render /not-registered first.
      if (!isRedirectingToError) setLoading(false);
    }
  }, [call, isAuthenticated, ready]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <UserContext.Provider value={{ user, loading, refreshUser: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
}
